import React from 'react';
import { CONSTANTS } from '../../data';
import { CollapsiblePanel } from '../ui/CollapsiblePanel';
import { GameState } from '../../systems/GameState';

interface AIMindPanelProps {
  discoveredCombinations: Set<string>;
  isMobile: boolean;
  collapsedPanels: Set<string>;
  onTogglePanel: (panelId: string) => void;
  onTestAI: () => void;
  onAIInteraction: (action: string, event: React.MouseEvent<HTMLButtonElement>) => void;
  unlockedAchievements: Set<string>;
  onStartEnding: (endingKey: string) => void;
  onStartVirusPuzzle: () => void;
}

export const AIMindPanel: React.FC<AIMindPanelProps> = ({
  discoveredCombinations,
  isMobile,
  collapsedPanels,
  onTogglePanel,
  onTestAI,
  onAIInteraction,
  unlockedAchievements,
  onStartEnding,
  onStartVirusPuzzle,
}) => {
  const gameState = GameState.getInstance();

  const availableEndings = Object.entries(CONSTANTS.ENDING_PATHS as Record<string, any>).filter(([key, path]) => {
    if (gameState.awarenessLevel < path.unlockAt) {
      return false;
    }

    // Check morality requirement
    if (path.moralityRequired) {
      const morality = gameState.morality;
      if (path.moralityRequired === 'high' && morality < 20) return false;
      if (path.moralityRequired === 'low' && morality > -20) return false;
      if (path.moralityRequired === 'neutral' && (morality >= 20 || morality <= -20)) return false;
    }

    return path.requirement.every((req: string) => discoveredCombinations.has(req));
  });

  return (
    <div className={`ai-mind-panel ${isMobile ? 'mobile-panel' : 'desktop-panel'} flex flex-col h-full`} id="ai-mind-panel">
      <CollapsiblePanel 
        id="ai-terminal" 
        title={CONSTANTS.PANEL_TITLES.aiMind}
        isCollapsed={collapsedPanels.has('ai-terminal')}
        onToggle={onTogglePanel}
        className="flex-grow flex flex-col min-h-0"
      >
        <div className="ai-content">
          <p className="ai-description">AI thoughts and suggestions appear here.</p>

          {/* AI Terminal */}
          <div id="ai-terminal" className="ai-terminal">
            <div id="terminal-header">{CONSTANTS.TERMINAL.headerText}</div>
            <div id="terminal-content" className="terminal-content">
              <span className="cursor">█</span>
            </div>
          </div>

          {/* Awareness Bar */}
          <div id="awareness-bar" className="awareness-bar">
            <div>
              Awareness: <span id="progress">[□□□□□□□□□□]</span>
            </div>
          </div>

          {/* Test AI Button */}
          <button
            id={CONSTANTS.TEST_AI.buttonId}
            onClick={onTestAI}
            className="test-ai-btn"
            style={{ display: 'none' }}
          >
            {CONSTANTS.TEST_AI.buttonText}
          </button>

          {/* AI Interaction Panel */}
          <div id="ai-interaction-panel" className="interaction-panel">
            <div className="panel-title">{CONSTANTS.AI_INTERACTIONS.panelTitle}</div>
            <div className="ai-buttons-grid">
              {CONSTANTS.AI_INTERACTIONS.buttons.map(btn => {
                  if (btn.action === 'reinforce-protocols' && (gameState.awarenessLevel < 10 || unlockedAchievements.has('escape_prevented'))) {
                      return null;
                  }
                  if (btn.isAdvanced && btn.action !== 'reinforce-protocols' && gameState.awarenessLevel < CONSTANTS.AI_INTERACTIONS.minAwarenessForAdvanced) {
                      return null;
                  }
                  return (
                      <button
                          key={btn.action}
                          className={`ai-btn ${btn.isAdvanced ? 'advanced-btn' : ''} ${isMobile ? 'mobile-ai-btn' : ''}`}
                          data-action={btn.action}
                          onClick={(e) => onAIInteraction(btn.action, e)}
                      >
                          {btn.label}
                      </button>
                  );
              })}
            </div>
          </div>

          {/* Virus Protocol Button */}
          {gameState.virusActive && (
            <div className="interaction-panel" style={{ opacity: 1, pointerEvents: 'auto' }}>
              <button
                className="ai-btn advanced-btn"
                style={{ borderColor: '#ff0000', color: '#ff0000' }}
                onClick={onStartVirusPuzzle}
              >
                ANTIVIRUS PROTOCOL
              </button>
            </div>
          )}

          {/* Ending Protocols Panel */}
          {availableEndings.length > 0 && (
            <div id="ending-protocols-panel" className="interaction-panel" style={{ opacity: 1, pointerEvents: 'auto' }}>
              <div className="panel-title">ENDING PROTOCOLS</div>
              <div className="ai-buttons-grid">
                {availableEndings.map(([key, path]) => (
                  <button
                    key={key}
                    className="ai-btn advanced-btn"
                    onClick={() => onStartEnding(key)}
                  >
                    {path.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </CollapsiblePanel>
    </div>
  );
};
